import type { FC, MouseEvent } from 'react'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../../components/Buttons/Button'
import { InfoBlock } from '../../components/InfoBlock/InfoBlock'
import { LoadingIndicator } from '../../components/LoadingIndicator/LoadingIndicator'
import ConfirmationModal from '../../components/Modals/ConfirmationModal'
import VehicleReadinessWarningModal from '../../components/Modals/VehicleReadinessWarningModal'
import { VehiclesGrid, VehiclesGridItem } from '../../components/VehiclesGrid/VehiclesGrid'
import ClockIcon from '../../assets/images/clock_rg_b_48_def.png'
import { deleteConsentThunk, fetchConsentsThunk } from '../../features/consents/consentsSlice'
import {
  selectConsentsError,
  selectConsentsItems,
  selectConsentsLoading,
  selectRevokingConsentId,
} from '../../features/consents/selectors/consentsSelectors'
import { buildConsentPortalUrl } from '../../features/consents/utils/consentPortalUrl'
import {
  formatConsentReadinessTime,
  getConsentReadinessRemainingMs,
} from '../../features/consents/utils/consentReadiness'
import { setCurrentVehicle } from '../../features/vehicle/vehicleSlice'
import { useAppDispatch, useAppSelector } from '../../hooks'
import { showError } from '../../utils/ErrorHandler'
import './styles.css'

const VehicleSelectionPage: FC = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const items = useAppSelector(selectConsentsItems)
  const loading = useAppSelector(selectConsentsLoading)
  const revokingConsentId = useAppSelector(selectRevokingConsentId)
  const error = useAppSelector(selectConsentsError)

  const [selectedCardId, setSelectedCardId] = useState<string | null>(null)
  const [pendingRevokeConsentId, setPendingRevokeConsentId] = useState<string | null>(null)
  const [pendingWarningConsentId, setPendingWarningConsentId] = useState<string | null>(null)
  const [nowMs, setNowMs] = useState(() => Date.now())

  useEffect(() => {
    void dispatch(fetchConsentsThunk())
  }, [dispatch])

  useEffect(() => {
    if (!error) return
    showError(error)
  }, [error])

  const consentCards = useMemo(
    () =>
      items.flatMap((record) =>
        record.consents.map((consent) => ({
          cardId: consent.id,
          consentId: consent.id,
          vin: record.vehicle.vin,
          model: record.vehicle.model,
          imageUrl: record.vehicle.imageUrl ?? null,
          createdAtTimestamp: consent.created_at_timestamp,
        })),
      ),
    [items],
  )

  const selectedCard = useMemo(
    () => consentCards.find((card) => card.cardId === selectedCardId),
    [consentCards, selectedCardId],
  )

  const warningCard = useMemo(
    () => consentCards.find((card) => card.consentId === pendingWarningConsentId),
    [consentCards, pendingWarningConsentId],
  )

  const selectedCardReadinessRemainingMs = selectedCard
    ? getConsentReadinessRemainingMs(selectedCard.createdAtTimestamp, nowMs)
    : 0

  const warningCardReadinessRemainingMs = warningCard
    ? getConsentReadinessRemainingMs(warningCard.createdAtTimestamp, nowMs)
    : 0

  const hasActiveReadinessCountdown = useMemo(
    () =>
      consentCards.some(
        (card) => getConsentReadinessRemainingMs(card.createdAtTimestamp, nowMs) > 0,
      ),
    [consentCards, nowMs],
  )

  useEffect(() => {
    if (!hasActiveReadinessCountdown) return undefined

    const intervalId = window.setInterval(() => {
      setNowMs(Date.now())
    }, 1000)

    return () => window.clearInterval(intervalId)
  }, [hasActiveReadinessCountdown])

  const handleRefresh = async () => {
    await dispatch(fetchConsentsThunk())
  }

  const handleCreateConsent = async () => {
    window.location.href = buildConsentPortalUrl()
  }

  const handleSelectVehicleCard = (cardId: string) => {
    const selected = consentCards.find((card) => card.cardId === cardId)
    if (!selected) return

    setSelectedCardId(selected.cardId)
    dispatch(
      setCurrentVehicle({
        vin: selected.vin,
        modelName: selected.model,
        imageUrl: selected.imageUrl,
        consentId: selected.consentId,
      }),
    )
  }

  const handleGoToChargingSteering = () => {
    if (!selectedCard) return

    if (selectedCardReadinessRemainingMs > 0) {
      setPendingWarningConsentId(selectedCard.consentId)
      return
    }

    navigate(`/charging-steering?vin=${encodeURIComponent(selectedCard.vin)}`)
  }

  const handleCancelReadinessWarning = () => {
    setPendingWarningConsentId(null)
  }

  const handleContinueToChargingSteering = () => {
    if (!warningCard) return

    setPendingWarningConsentId(null)
    navigate(`/charging-steering?vin=${encodeURIComponent(warningCard.vin)}`)
  }

  const handleOpenRevokeModal = (event: MouseEvent, consentId: string) => {
    event.stopPropagation()
    setPendingRevokeConsentId(consentId)
  }

  const handleCloseRevokeModal = () => {
    setPendingRevokeConsentId(null)
  }

  const handleConfirmRevoke = async () => {
    if (!pendingRevokeConsentId) return
    await dispatch(deleteConsentThunk(pendingRevokeConsentId))
    setPendingRevokeConsentId(null)
  }

  const isConfirmLoading =
    pendingRevokeConsentId != null && revokingConsentId === pendingRevokeConsentId

  return (
    <section className="vehicle-selection-page" aria-label="Vehicle selection page">
      <header className="vehicle-selection-header">
        <h1 className="vehicle-selection-title">Vehicle Selection</h1>
        <p className="vehicle-selection-subtitle">
          Refresh/create consents, choose a vehicle, then continue to charging steering
        </p>
      </header>

      <div className="vehicle-selection-actions">
        <Button
          variant="secondary"
          onClick={() => void handleRefresh()}
          disabled={loading}
          className="vehicle-selection-refresh-button"
          aria-label="Refresh consents"
          title="Refresh consents"
        >
          {loading ? (
            <LoadingIndicator style="INLINE" />
          ) : (
            <span className="vehicle-selection-refresh-icon" />
          )}
        </Button>

        <Button
          className="vehicle-selection-create-consent-button"
          variant="primary"
          onClick={() => void handleCreateConsent()}
        >
          Create Consent
        </Button>

        <Button
          variant="secondary"
          onClick={handleGoToChargingSteering}
          disabled={!selectedCard}
          className="vehicle-selection-proceed-button"
          aria-label="Go to charging steering"
          title="Go to charging steering"
        >
          <span className="vehicle-selection-proceed-icon" />
        </Button>
      </div>

      {consentCards.length === 0 && !loading ? (
        <InfoBlock
          title="No Active Consents"
          description="You currently have no consented vehicles. Create a consent first to continue to charging steering."
        />
      ) : null}

      <VehiclesGrid>
        {consentCards.map((card) => {
          const isRevoking = revokingConsentId === card.consentId
          const readinessRemainingMs = getConsentReadinessRemainingMs(
            card.createdAtTimestamp,
            nowMs,
          )
          const shouldShowReadinessTimer = readinessRemainingMs > 0

          return (
            <VehiclesGridItem
              key={card.cardId}
              selectionId={card.cardId}
              vehicle={{ vin: card.vin, model: card.model, imageUrl: card.imageUrl }}
              isSelected={selectedCardId === card.cardId}
              onSelect={handleSelectVehicleCard}
              topLeftContent={
                shouldShowReadinessTimer ? (
                  <img
                    src={ClockIcon}
                    alt=""
                    aria-hidden="true"
                    className="vehicle-selection-readiness-card-clock"
                  />
                ) : undefined
              }
              topContent={
                shouldShowReadinessTimer ? (
                  <div className="vehicle-selection-readiness-timer">
                    <img
                      src={ClockIcon}
                      alt=""
                      aria-hidden="true"
                      className="vehicle-selection-readiness-timer-icon"
                    />
                    <span>{formatConsentReadinessTime(readinessRemainingMs)} min</span>
                  </div>
                ) : undefined
              }
            >
              <p className="vehicle-selection-consent-count">Consent ID: {card.consentId}</p>
              <div className="vehicle-selection-consents-list">
                <div className="vehicle-selection-consent-row">
                  <span className="vehicle-selection-consent-id" title={card.consentId}>
                    {card.consentId}
                  </span>
                  <Button
                    variant="secondary"
                    className="vehicle-selection-revoke-button"
                    disabled={isRevoking}
                    onClick={(event) => handleOpenRevokeModal(event, card.consentId)}
                  >
                    {isRevoking ? <LoadingIndicator style="INLINE" /> : 'Revoke'}
                  </Button>
                </div>
              </div>
            </VehiclesGridItem>
          )
        })}
      </VehiclesGrid>

      <ConfirmationModal
        isOpen={pendingRevokeConsentId != null}
        title="Revoke Consent"
        message="Are you sure you want to revoke this consented vehicle access?"
        onConfirm={() => void handleConfirmRevoke()}
        onClose={handleCloseRevokeModal}
        isConfirmLoading={isConfirmLoading}
      />

      <VehicleReadinessWarningModal
        isOpen={pendingWarningConsentId != null && warningCardReadinessRemainingMs > 0}
        remainingMs={warningCardReadinessRemainingMs}
        onCancel={handleCancelReadinessWarning}
        onContinue={handleContinueToChargingSteering}
      />
    </section>
  )
}

export default VehicleSelectionPage
